import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Download, ArrowLeft } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Turbine } from '../types';
import { getProjectMetadata } from '../utils/r2Storage';

// Fix leaflet default marker icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const PageContainer = styled.div`
  padding: 30px;
  max-width: 1200px;
  margin: 0 auto;
`;

const BackButton = styled.button`
  background: white;
  border: 1px solid #e5e7eb;
  padding: 8px 16px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #1a1f3a;
  margin-bottom: 20px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f9fafb;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  color: #6b7280;
  
  a {
    color: #0099ff;
    text-decoration: none;
    cursor: pointer;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;
  
  div {
    h1 {
      font-size: 32px;
      font-weight: 700;
      color: #1a1f3a;
      margin: 0 0 8px 0;
    }
    
    p {
      color: #6b7280;
      margin: 0;
      font-size: 14px;
    }
  }
`;

const DownloadButton = styled.button`
  background: #0099ff;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 153, 255, 0.3);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const InfoSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 30px;
  
  h2 {
    font-size: 14px;
    font-weight: 700;
    color: #6b7280;
    margin: 0 0 24px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px 60px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  
  label {
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
  }
  
  span {
    font-size: 14px;
    color: #1a1f3a;
  }
`;

const StatusBadge = styled.span<{ $status?: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.$status === 'Completed' ? '#d1fae5' : '#fef3c7'};
  color: ${props => props.$status === 'Completed' ? '#065f46' : '#92400e'};
`;

const MapSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 30px;
  
  h2 {
    font-size: 14px;
    font-weight: 700;
    color: #6b7280;
    margin: 0 0 16px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const MapContainer2 = styled.div`
  height: 400px;
  border-radius: 8px;
  overflow: hidden;
`;

const InspectionsSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  
  h2 {
    font-size: 14px;
    font-weight: 700;
    color: #6b7280;
    margin: 0 0 16px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th {
    text-align: left;
    padding: 12px;
    background: #f9fafb;
    color: #6b7280;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid #e5e7eb;
  }
  
  td {
    padding: 12px;
    border-top: 1px solid #f3f4f6;
    color: #1a1f3a;
    font-size: 14px;
  }
  
  tr {
    cursor: pointer;
    transition: background 0.2s ease;
    
    &:hover {
      background: #f9fafb;
    }
  }
`;

const TurbineDetail: React.FC = () => {
  const { turbineId } = useParams<{ turbineId: string }>();
  const navigate = useNavigate();
  const [turbine, setTurbine] = useState<Turbine | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTurbine();
  }, [turbineId]);

  const loadTurbine = async () => {
    try {
      const metadata: any = await getProjectMetadata('turbines-list');
      if (metadata && metadata.turbines) {
        const foundTurbine = metadata.turbines.find((t: Turbine) => t.id === turbineId);
        if (foundTurbine) {
          setTurbine(foundTurbine);
        }
      }
    } catch (error) {
      console.error('Failed to load turbine:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInspectionClick = (inspectionId: string) => {
    navigate(`/project/${inspectionId}`);
  };

  const handleDownloadReport = () => {
    alert('Download functionality will be implemented');
  };

  if (loading) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <p style={{ color: '#6b7280' }}>Loading turbine details...</p>
        </div>
      </PageContainer>
    );
  }

  if (!turbine) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <p style={{ color: '#ef4444' }}>Turbine not found</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackButton onClick={() => navigate('/turbines')}>
        <ArrowLeft />
        Back
      </BackButton>

      <Breadcrumb>
        <a onClick={() => navigate('/')}>Dashboard</a>
        <span>/</span>
        <a onClick={() => navigate('/wind-farms')}>Wind Farms</a>
        <span>/</span>
        <a onClick={() => navigate('/turbines')}>Turbines</a>
      </Breadcrumb>

      <Header>
        <div>
          <h1>TURBINE DETAILS</h1>
          <p>{turbine.number} turbine details</p>
        </div>
        <DownloadButton onClick={handleDownloadReport}>
          <Download />
          Download Report
        </DownloadButton>
      </Header>

      <InfoSection>
        <h2>{turbine.number} TURBINE INFORMATIONS</h2>
        <InfoGrid>
          <InfoItem>
            <label>Turbine Number:</label>
            <span>{turbine.number}</span>
          </InfoItem>
          <InfoItem>
            <label>Wind Farm:</label>
            <span>Demo RES</span>
          </InfoItem>
          <InfoItem>
            <label>Model:</label>
            <span>{turbine.model}</span>
          </InfoItem>
          <InfoItem>
            <label>Country/City:</label>
            <span>Turkey/İzmir</span>
          </InfoItem>
          <InfoItem>
            <label>Rotor Diameter:</label>
            <span>{turbine.rotorDiameter}m</span>
          </InfoItem>
          <InfoItem>
            <label>Last Inspection Status:</label>
            <span>
              <StatusBadge $status={turbine.inspectionId ? 'Completed' : 'Pending'}>
                {turbine.inspectionId ? 'Completed' : 'Pending'}
              </StatusBadge>
              {!turbine.inspectionId && (
                <StatusBadge $status="Damaged" style={{ marginLeft: '8px' }}>
                  52 Damage(s) Detected
                </StatusBadge>
              )}
            </span>
          </InfoItem>
          <InfoItem>
            <label>Blade Model:</label>
            <span>{turbine.bladeModel}</span>
          </InfoItem>
          <InfoItem>
            <label>Last Inspection Date:</label>
            <span>{turbine.lastInspectionDate ? new Date(turbine.lastInspectionDate).toLocaleDateString() : 'N/A'}</span>
          </InfoItem>
          <InfoItem>
            <label>Blade Length:</label>
            <span>{turbine.bladeLength}m</span>
          </InfoItem>
          <InfoItem>
            <label>Coordinates:</label>
            <span>{turbine.location.lat.toFixed(6)}, {turbine.location.lng.toFixed(6)}</span>
          </InfoItem>
        </InfoGrid>
      </InfoSection>

      <MapSection>
        <h2>LOCATION</h2>
        <MapContainer2>
          <MapContainer
            center={[turbine.location.lat, turbine.location.lng]}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={[turbine.location.lat, turbine.location.lng]}>
              <Popup>{turbine.name}</Popup>
            </Marker>
          </MapContainer>
        </MapContainer2>
      </MapSection>

      {turbine.inspectionId && (
        <InspectionsSection>
          <h2>{turbine.number} INSPECTIONS</h2>
          <Table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Inspection Code</th>
                <th>Date</th>
                <th>Inspection Status</th>
              </tr>
            </thead>
            <tbody>
              <tr onClick={() => handleInspectionClick(turbine.inspectionId!)}>
                <td>1</td>
                <td>{turbine.inspectionId}</td>
                <td>{turbine.lastInspectionDate ? new Date(turbine.lastInspectionDate).toLocaleDateString() : 'N/A'}</td>
                <td>Completed</td>
              </tr>
            </tbody>
          </Table>
        </InspectionsSection>
      )}
    </PageContainer>
  );
};

export default TurbineDetail;
